import  { useState, useRef } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapMarkerAlt, faEnvelope, faPhoneAlt } from '@fortawesome/free-solid-svg-icons';
import { faLinkedinIn, faTwitter, faFacebookF, faInstagram } from '@fortawesome/free-brands-svg-icons';



export default function ContactPage() {
  // --- FORM STATE (from bottom code, adapted) ---
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
    gender: "", // kept in case you want to add it later; not rendered right now
  });

  const [errors, setErrors] = useState({});
  const [responseMsg, setResponseMsg] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formDisabled, setFormDisabled] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // --- ORIGINAL STATE ---
  const [fileName, setFileName] = useState("");
  const [fileActive, setFileActive] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [emailValid, setEmailValid] = useState(null);
  const [loading, setLoading] = useState(false);

  const fileRef = useRef(null);
  const formRef = useRef(null);

  // ------------ FILE HANDLERS ------------
  function handleFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    const sizeMB = file.size / 1024 / 1024;
    if (sizeMB > 5) {
      alert("File exceeds 5MB limit");
      e.target.value = "";
      return;
    }
    setFileName(file.name);
    setFileActive(true);
  }

  function removeFile() {
    if (fileRef.current) fileRef.current.value = "";
    setFileActive(false);
    setFileName("");
  }

  function preventDefaults(e) {
    e.preventDefault();
    e.stopPropagation();
  }

  function handleDrop(e) {
    preventDefaults(e);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;

    const dt = new DataTransfer();
    dt.items.add(file);
    fileRef.current.files = dt.files;

    fileRef.current.dispatchEvent(new Event("change", { bubbles: true }));
  }

  // ------------ FORM DATA HANDLING ------------
  function handleChange(e) {
    const { name, value } = e.target;

    // update generic form fields
    setFormData((prev) => ({ ...prev, [name]: value }));

    // clear specific field error
    setErrors((prev) => ({ ...prev, [name]: "" }));

    // keep original emailValid behaviour
    if (name === "email") {
      setEmailValid(null);
    }
  }
const [open, setOpen] = useState(false);
  function handleEmailBlur(e) {
    const v = e.target.value;
    if (!v) {
      setEmailValid(null);
      return;
    }

    const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const valid = pattern.test(v);
    setEmailValid(valid);

    // also sync with errors map like bottom code
    setErrors((prev) => ({
      ...prev,
      email: !valid ? "Invalid email format." : "",
    }));
  }

  // ------------ VALIDATION (from bottom code, adapted) ------------
  function validateForm() {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = "Name is required.";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required.";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Invalid email format.";
    }
    if (!formData.subject.trim()) newErrors.subject = "Subject is required.";
    if (!formData.message.trim())
      newErrors.message = "Message cannot be empty.";

    // gender is optional in the upper UI, but if you want it required:
    // if (!formData.gender) newErrors.gender = "Gender selection is required.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  // ------------ SUBMIT (merged logic) ------------
  async function submitForm(e) {
    e.preventDefault();
    setResponseMsg("");
    setIsSuccess(false);

    if (!validateForm()) return;

    setIsSubmitting(true);
    setFormDisabled(true);
    setLoading(true); // for the Tailwind spinner

    // NOTE: this only sends text fields, not the file. To send the file,
    // you must switch to FormData + backend support for multipart/form-data.
    const dataToSend = {
      name: formData.name,
      email: formData.email,
      subject: formData.subject,
      message: formData.message,
      // gender: formData.gender, // include if used
    };

    const formDataEncoded = new URLSearchParams(dataToSend).toString();

    try {
      const response = await fetch("/config.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: formDataEncoded,
      });

      const result = await response.text();

      if (result === "success") {
        setIsSuccess(true);
        setResponseMsg("Your message has been sent successfully!");
        setShowSuccess(true);

        // reset UI
        setFormData({
          name: "",
          email: "",
          subject: "",
          message: "",
          gender: "",
        });
        setEmailValid(null);
        setErrors({});
        if (formRef.current) formRef.current.reset();
        removeFile();

        // hide success panel after some time (same idea as original)
        setTimeout(() => {
          setShowSuccess(false);
        }, 4000);
      } else {
        setIsSuccess(false);
        setResponseMsg("Something went wrong. Please try again.");
        setFormDisabled(false);
      }
    } catch (error) {
      console.error("Error connecting to the server:", error);
      setIsSuccess(false);
      setResponseMsg(
        "Error connecting to the server. Please try again later."
      );
      setFormDisabled(false);
    } finally {
      setIsSubmitting(false);
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#f7f9fb] font-sans text-[#333]">
      {/* HEADER */}
      <header className="text-center text-white py-[60px] px-5 bg-gradient-to-br from-[#048998] to-[#006B77] shadow-md">
        <h1 className="text-[clamp(2rem,5vw,2.8rem)] font-extrabold">
          Contact Us
        </h1>
        <p className="text-[clamp(1rem,2.5vw,1.1rem)] opacity-95 mt-2">
          Let's Connect & Grow Together
        </p>
      </header>

      {/* MAIN CONTENT */}
      <main className="max-w-[1200px] mx-auto -mt-10 mb-20 px-5">
        <div className="grid lg:grid-cols-[58%_42%] gap-12 mt-16">
          {/* FORM CARD */}
          <div className="bg-white rounded-[12px] p-11 shadow-md">
            {!showSuccess ? (
              <form ref={formRef} onSubmit={submitForm} className="space-y-6">
                {/* FULL NAME */}
                <div>
                  <label className="block mb-2 font-semibold text-sm text-[#2b2d42]">
                    Full Name <span className="text-[#048998]">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="John Doe"
                    value={formData.name}
                    onChange={handleChange}
                    disabled={formDisabled}
                    className="w-full px-4 py-3 border-2 border-[#e0e0e0] rounded-lg text-base focus:outline-none 
                    focus:border-[#048998] focus:shadow-[0_0_0_3px_rgba(0,131,143,0.1)]"
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-[#EF5350]">
                      {errors.name}
                    </p>
                  )}
                </div>

                {/* EMAIL */}
                <div>
                  <label className="block mb-2 font-semibold text-sm text-[#2b2d42]">
                    Email Address <span className="text-[#048998]">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="john@example.com"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={handleEmailBlur}
                    disabled={formDisabled}
                    className={`w-full px-4 py-3 border-2 rounded-lg text-base focus:outline-none 
                      focus:shadow-[0_0_0_3px_rgba(0,131,143,0.1)]
                      ${
                        emailValid === true
                          ? "border-[#4CAF50]"
                          : emailValid === false
                          ? "border-[#EF5350]"
                          : "border-[#e0e0e0]"
                      }`}
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-[#EF5350]">
                      {errors.email}
                    </p>
                  )}
                </div>

                {/* SUBJECT */}
                

                <div>
  <label className="block mb-2 font-semibold text-sm text-[#2b2d42]">
    Subject <span className="text-[#048998]">*</span>
  </label>

  <div className="relative w-full">
    <select
      name="subject"
      required
      value={formData.subject}
      onChange={handleChange}
      disabled={formDisabled}
      onFocus={() => setOpen(true)} // rotate arrow when focused
      onBlur={() => {
        // add small delay to ensure rotation back
        setTimeout(() => setOpen(false), 100);
      }}
      className="w-full px-4 py-3 border-2 border-[#e0e0e0] rounded-lg 
                 bg-white text-base appearance-none pr-10"
    >
      <option value="">Choose Your Subject</option>
      <option value="general">General Enquiry</option>
      <option value="publisher">Publishers Support</option>
      <option value="advertiser">Advertiser Partnership</option>
      <option value="billing">Billing Issue</option>
      <option value="suspended">Account Suspended</option>
      <option value="other">Others</option>
    </select>

    {/* Rotating Arrow */}
    <div
      className={`
        absolute right-4 top-1/2 -translate-y-1/2 
        transition-transform duration-300 
        pointer-events-none
        ${open ? "rotate-180" : "rotate-0"}
      `}
    >
      ▼
    </div>
  </div>

  {errors.subject && (
    <p className="mt-1 text-sm text-[#EF5350]">
      {errors.subject}
    </p>
  )}
</div>


                {/* MESSAGE */}
                <div>
                  <label className="block mb-2 font-semibold text-sm text-[#2b2d42]">
                    Your Message <span className="text-[#048998]">*</span>
                  </label>
                  <textarea
                    name="message"
                    required
                    placeholder="How can we help you?"
                    value={formData.message}
                    onChange={handleChange}
                    disabled={formDisabled}
                    className="w-full px-4 py-3 min-h-[140px] border-2 border-[#e0e0e0] rounded-lg text-base 
                    resize-y focus:outline-none focus:border-[#048998] 
                    focus:shadow-[0_0_0_3px_rgba(0,131,143,0.1)]"
                  />
                  {errors.message && (
                    <p className="mt-1 text-sm text-[#EF5350]">
                      {errors.message}
                    </p>
                  )}
                </div>

                {/* FILE UPLOAD */}
                <div>
                  <label className="block mb-2 font-semibold text-sm text-[#2b2d42]">
                    Attach File{" "}
                    <span className="text-[#9a9bab] text-sm">(Optional)</span>
                  </label>

                  <div
                    onDragEnter={preventDefaults}
                    onDragOver={preventDefaults}
                    onDragLeave={preventDefaults}
                    onDrop={preventDefaults}
                  >
                    <label
                      htmlFor="attachment"
                      onDrop={handleDrop}
                      className="flex items-center gap-3 px-4 py-3 border-2 border-dashed border-[#e0e0e0] 
                      rounded-lg cursor-pointer transition-colors bg-[#fafbfc] hover:border-[#048998]"
                    >
                      <i className="fas fa-cloud-upload-alt text-[#048998] text-xl"></i>
                      <div className="text-[#757575] text-sm">
                        <span className="text-[#048998] font-semibold">
                          Choose a file
                        </span>{" "}
                        or drag it here
                        <div className="text-[12px] text-[#9a9bab] mt-1">
                          Max file size: 5MB (JPG, PNG, PDF, DOC)
                        </div>
                      </div>
                    </label>

                    <input
                      type="file"
                      id="attachment"
                      ref={fileRef}
                      accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                      className="hidden"
                      onChange={handleFileChange}
                      disabled={formDisabled}
                    />

                    {fileActive && (
                      <div className="mt-3 p-2 rounded-md bg-[#e8f4f5] text-[#048998] text-sm flex items-center gap-2">
                        <i className="fas fa-file-alt text-[#4CAF50]"></i>
                        <span>{fileName}</span>
                        <i
                          className="fas fa-times ml-auto text-[#EF5350] cursor-pointer"
                          onClick={removeFile}
                        ></i>
                      </div>
                    )}
                  </div>
                </div>

                {/* SUBMIT BUTTON */}
                <button
                  type="submit"
                  className={`w-full py-4 rounded-lg text-white font-semibold uppercase tracking-wide 
                  transition-all bg-gradient-to-b from-[#048998] to-[#006B77] shadow-lg
                  ${
                    loading || isSubmitting || formDisabled
                      ? "opacity-60 pointer-events-none"
                      : ""
                  }`}
                  disabled={loading || isSubmitting || formDisabled}
                >
                  {loading || isSubmitting ? (
                    <span className="inline-block w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    "Submit"
                  )}
                </button>

                {/* RESPONSE MESSAGE (from bottom code, styled) */}
                {responseMsg && (
                  <p
                    className={`mt-4 px-4 py-3 rounded text-sm ${
                      isSuccess
                        ? "bg-green-50 text-green-700 border border-green-200"
                        : "bg-red-50 text-red-700 border border-red-200"
                    }`}
                  >
                    {responseMsg}
                  </p>
                )}
              </form>
            ) : (
              <div className="text-center py-16 px-6">
                <div className="mx-auto w-[80px] h-[80px] rounded-full flex items-center justify-center bg-[#4CAF50] text-white text-3xl mb-6">
                  <i className="fas fa-check"></i>
                </div>
                <h2 className="text-2xl font-bold text-[#2b2d42] mb-3">
                  Message Sent Successfully!
                </h2>
                <p className="text-[#757575] mb-1">
                  Thank you for contacting ClickHunts.
                </p>
                <p className="text-[#757575] mb-3">
                  Our team will respond within 24 hours.
                </p>
                <p className="mt-5 text-[#048998] font-semibold">
                  We've sent a confirmation email to your inbox.
                </p>
              </div>
            )}
          </div>

          {/* SIDEBAR (unchanged) */}
          <aside className="bg-white rounded-[12px] p-10 shadow-md border-l-4 border-[#048998]">
  <h3 className="text-[#048998] text-xs font-semibold tracking-wider uppercase mb-2">
    Business Development
  </h3>
  <h2 className="text-2xl font-bold text-[#2b2d42] mb-8">
    ClickHunts LLC.
  </h2>

  {/* Address */}
  <div className="mb-6 p-6 bg-[#f8fafb] rounded-lg border-l-4 border-[#048998]">
    <div className="flex gap-4 items-start">
      <div className="w-11 h-11 rounded-full bg-[rgba(0,131,143,0.1)] flex items-center justify-center text-[#048998] text-lg">
        <FontAwesomeIcon icon={faMapMarkerAlt} />
      </div>
      <div>
        <h4 className="text-[#048998] font-semibold">ClickHunts</h4>
        <p className="text-[#424242] text-sm">
          30 N Gould Street Suite #41562
        </p>
        <p className="text-[#424242] text-sm">Sheridan, WY 82801</p>
        <p className="text-[#424242] text-sm">United States</p>
      </div>
    </div>
  </div>

  {/* Email */}
  <div className="mb-6 p-6 bg-[#f8fafb] rounded-lg border-l-4 border-[#048998]">
    <div className="flex gap-4 items-start">
      <div className="w-11 h-11 rounded-full bg-[rgba(0,131,143,0.1)] flex items-center justify-center text-[#048998] text-lg">
        <FontAwesomeIcon icon={faEnvelope} />
      </div>
      <div>
        <h4 className="text-[#048998] font-semibold">Email Us</h4>
        <p className="text-[#424242] text-sm">
          <a
            href="mailto:support@clickhunts.com"
            className="hover:text-[#048998]"
          >
            support@clickhunts.com
          </a>
        </p>
        <p className="text-[#424242] text-sm">
          <a
            href="mailto:partners@clickhunts.com"
            className="hover:text-[#048998]"
          >
            partners@clickhunts.com
          </a>
        </p>
      </div>
    </div>
  </div>

  {/* Phone */}
  <div className="mb-6 p-6 bg-[#f8fafb] rounded-lg border-l-4 border-[#048998]">
    <div className="flex gap-4 items-start">
      <div className="w-11 h-11 rounded-full bg-[rgba(0,131,143,0.1)] flex items-center justify-center text-[#048998] text-lg">
        <FontAwesomeIcon icon={faPhoneAlt} />
      </div>
      <div>
        <h4 className="text-[#048998] font-semibold">Call Us</h4>
        <p className="text-[#424242] text-sm">
          <a
            href="tel:+3074001096"
            className="hover:text-[#048998]"
          >
            +1 (307) 400-1096
          </a>
        </p>
        <p className="text-[#424242] text-sm">
          Mon - Fri, 9am - 6pm EST
        </p>
      </div>
    </div>
  </div>

  {/* SOCIALS */}
  <div className="pt-6 border-t-2 border-[#e8f4f5] mt-6">
    <h4 className="text-[#048998] font-semibold mb-3">Follow Us</h4>
    <div className="flex gap-3">
      <a
        href="https://www.linkedin.com/company/clickhunts"
        className="w-11 h-11 rounded-full bg-[rgba(0,131,143,0.1)] flex items-center justify-center text-[#048998] hover:bg-[#048998] hover:text-white transition"
      >
        <FontAwesomeIcon icon={faLinkedinIn} />
      </a>

      <a
        href="https://x.com/clickhunts"
        className="w-11 h-11 rounded-full bg-[rgba(0,131,143,0.1)] flex items-center justify-center text-[#048998] hover:bg-[#048998] hover:text-white transition"
      >
        <FontAwesomeIcon icon={faTwitter} />
      </a>

      <a
        href="https://www.facebook.com/clickhuntsnetwork"
        className="w-11 h-11 rounded-full bg-[rgba(0,131,143,0.1)] flex items-center justify-center text-[#048998] hover:bg-[#048998] hover:text-white transition"
      >
        <FontAwesomeIcon icon={faFacebookF} />
      </a>

      <a
        href="https://www.instagram.com/click.hunts/"
        className="w-11 h-11 rounded-full bg-[rgba(0,131,143,0.1)] flex items-center justify-center text-[#048998] hover:bg-[#048998] hover:text-white transition"
      >
        <FontAwesomeIcon icon={faInstagram} />
      </a>
    </div>
  </div>
</aside>
        </div>

        {/* MAP */}
        <section className="mt-14 text-center">
          <h2 className="text-[#2b2d42] text-2xl font-semibold">Find Us Here</h2>
          <div className="w-10 h-[2px] bg-[#048998] mx-auto my-6"></div>

          <div className="rounded-lg overflow-hidden shadow-md border border-[#e0e0e0] h-[450px]">
            <iframe
              title="ClickHunts location"
              className="w-full h-full"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2889.9831917285546!2d-106.95859308450278!3d44.79659597909902!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x5335fabc2a66d7db%3A0xb07016f2bf769ffe!2s30%20N%20Gould%20St%2C%20Sheridan%2C%20WY%2082801%2C%20USA!5e0!3m2!1sen!2sbd!4v1234567890123!5m2!1sen!2sbd"
              allowFullScreen
              loading="lazy"
            ></iframe>
          </div>
        </section>
      </main>
    </div>
  );
}